/**
 * Totals Calculator Utility
 * 
 * Provides consistent calculation logic for cart totals across all POS components.
 * Handles both scenarios: prices including VAT and prices excluding VAT.
 * Properly applies discounts and recalculates subtotals and VAT accordingly.
 */

/**
 * Calculate cart totals with proper discount handling
 * 
 * @param {Array} cart - Array of cart items with Quantity, Price, PriceInclVAT, VAT properties
 * @param {Object} discount - Discount object with amount property
 * @param {number} customVATRate - Custom VAT rate override (if null, uses item.VAT)
 * @param {boolean} isTvaIncluded - Whether prices already include VAT
 * @returns {Object} { subtotal, discountAmount, totalVAT, totalIncl, originalTotal }
 */
export function calcTotals(cart, discount, customVATRate, isTvaIncluded) {
  if (!cart || cart.length === 0) {
    return { 
      subtotal: 0, 
      discountAmount: 0, 
      totalVAT: 0, 
      totalIncl: 0,
      originalTotal: 0,
      subtotalBeforeDiscount: 0
    };
  }
  
  const discountAmount = discount?.amount || 0;
  
  if (isTvaIncluded) {
    // Prices already include VAT
    // Subtotal = sum of prices including VAT
    const subtotalWithVAT = Math.round(
      cart.reduce((s, item) => s + item.Quantity * (item.PriceInclVAT ?? item.Price), 0) * 100
    ) / 100;
    
    // Apply discount to subtotal (which already includes VAT)
    const totalAfterDiscount = Math.max(0, Math.round((subtotalWithVAT - discountAmount) * 100) / 100);
    
    // Extract VAT from the total after discount
    const totalVAT = Math.round(
      cart.reduce((s, item) => {
        const lineTotal = item.Quantity * (item.PriceInclVAT ?? item.Price);
        const ratio = subtotalWithVAT > 0 ? lineTotal / subtotalWithVAT : 0;
        const lineAfterDiscount = Math.max(0, lineTotal - discountAmount * ratio);
        const rate = customVATRate != null ? customVATRate : item.VAT || 0;
        // Extract VAT: VAT = price / (1 + rate/100) * (rate/100)
        return s + (lineAfterDiscount * rate) / (100 + rate);
      }, 0) * 100
    ) / 100;
    
    // Calculate subtotal excluding VAT (base price after discount)
    const subtotalExclVAT = Math.round((totalAfterDiscount - totalVAT) * 100) / 100;
    
    return {
      subtotal: subtotalExclVAT,  // Base price excluding VAT after discount
      discountAmount: discountAmount,
      totalVAT: totalVAT,
      totalIncl: totalAfterDiscount,
      originalTotal: subtotalWithVAT,  // Original total with VAT before discount
      subtotalBeforeDiscount: subtotalWithVAT
    };
  } else {
    // Prices exclude VAT
    // Subtotal = sum of base prices (excluding VAT)
    const subtotal = Math.round(
      cart.reduce((s, item) => s + item.Quantity * item.Price, 0) * 100
    ) / 100;
    
    // Apply discount to subtotal
    const subtotalAfterDiscount = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100);
    
    // Calculate VAT on subtotal after discount
    const totalVAT = Math.round(
      cart.reduce((s, item) => {
        const lineTotal = item.Quantity * item.Price;
        const ratio = subtotal > 0 ? lineTotal / subtotal : 0;
        const lineAfterDiscount = Math.max(0, lineTotal - discountAmount * ratio);
        const rate = customVATRate != null ? customVATRate : item.VAT || 0;
        return s + (lineAfterDiscount * rate) / 100;
      }, 0) * 100
    ) / 100;
    
    // Total = subtotal after discount + VAT
    const totalIncl = Math.round((subtotalAfterDiscount + totalVAT) * 100) / 100;
    
    return {
      subtotal: subtotal,
      discountAmount: discountAmount,
      totalVAT: totalVAT,
      totalIncl: totalIncl,
      originalTotal: subtotal,  // Original subtotal before discount (excl VAT)
      subtotalBeforeDiscount: subtotal
    };
  }
}

/**
 * Calculate VAT breakdown by rate with proper discount distribution
 * 
 * @param {Array} cart - Array of cart items
 * @param {number} discountAmount - Total discount amount
 * @param {number} customVATRate - Custom VAT rate override
 * @param {boolean} isTvaIncluded - Whether prices include VAT
 * @returns {Object} Object with VAT rates as keys and { base, vat } as values
 */
export function calcVATBreakdown(cart, discountAmount, customVATRate, isTvaIncluded) {
  if (!cart || cart.length === 0) {
    return {};
  }

  const vatBreakdown = {};
  
  if (isTvaIncluded) {
    // Calculate total with VAT before discount for ratio calculation
    const totalWithVAT = cart.reduce((s, item) => 
      s + item.Quantity * (item.PriceInclVAT ?? item.Price), 0
    );
    
    cart.forEach((item) => {
      const vatRate = customVATRate != null ? customVATRate : parseFloat(item.VAT || 0);
      const lineTotal = item.Quantity * (item.PriceInclVAT ?? item.Price);
      const ratio = totalWithVAT > 0 ? lineTotal / totalWithVAT : 0;
      const lineAfterDiscount = Math.max(0, lineTotal - discountAmount * ratio);
      
      // Extract VAT from line total
      const vatAmount = (lineAfterDiscount * vatRate) / (100 + vatRate);
      const baseAmount = lineAfterDiscount - vatAmount;

      if (!vatBreakdown[vatRate]) {
        vatBreakdown[vatRate] = { base: 0, vat: 0 };
      }
      vatBreakdown[vatRate].base += baseAmount;
      vatBreakdown[vatRate].vat += vatAmount;
    });
  } else {
    // Calculate total (excl VAT) before discount for ratio calculation
    const subtotal = cart.reduce((s, item) => s + item.Quantity * item.Price, 0);
    
    cart.forEach((item) => {
      const vatRate = customVATRate != null ? customVATRate : parseFloat(item.VAT || 0);
      const itemTotal = item.Quantity * item.Price;
      const ratio = subtotal > 0 ? itemTotal / subtotal : 0;
      const baseAmount = Math.max(0, itemTotal - discountAmount * ratio);
      const vatAmount = (baseAmount * vatRate) / 100;

      if (!vatBreakdown[vatRate]) {
        vatBreakdown[vatRate] = { base: 0, vat: 0 };
      }
      vatBreakdown[vatRate].base += baseAmount;
      vatBreakdown[vatRate].vat += vatAmount;
    });
  }

  // Round all values to 2 decimal places
  Object.keys(vatBreakdown).forEach((rate) => {
    vatBreakdown[rate].base = Math.round(vatBreakdown[rate].base * 100) / 100;
    vatBreakdown[rate].vat = Math.round(vatBreakdown[rate].vat * 100) / 100;
  });

  return vatBreakdown;
}

/**
 * Calculate original cart total (before discount) for discount validation
 * 
 * @param {Array} cart - Array of cart items
 * @param {boolean} isTvaIncluded - Whether prices include VAT
 * @returns {number} Original total before discount
 */
export function calcOriginalTotal(cart, isTvaIncluded) {
  if (!cart || cart.length === 0) return 0;
  
  return Math.round(
    cart.reduce((s, item) => {
      const itemPrice = isTvaIncluded ? (item.PriceInclVAT ?? item.Price) : item.Price;
      return s + item.Quantity * itemPrice;
    }, 0) * 100
  ) / 100;
}
